import { Zap, Wrench, ShoppingBag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/shadcn/card'
import { Badge } from '@/components/shadcn/badge'
import type { ActionModuleResponse, BaseShopModuleModel, ServiceModuleModel } from '@/api/model'

function ActionModuleCard({ module }: { module: ActionModuleResponse }) {
  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex items-center gap-2 min-w-0">
        {module.iconUrl ? (
          <img src={module.iconUrl} alt="" className="size-6 object-contain shrink-0" />
        ) : (
          <Zap className="size-5 text-muted-foreground shrink-0" />
        )}
        <span className="font-medium text-sm truncate">{module.name || '—'}</span>
      </div>
      {module.description && (
        <p className="text-xs text-muted-foreground line-clamp-2">{module.description}</p>
      )}
    </Card>
  )
}

function ServiceModuleCard({ module }: { module: ServiceModuleModel }) {
  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex items-center gap-2 min-w-0">
        {module.iconUrl ? (
          <img src={module.iconUrl} alt="" className="size-6 object-contain shrink-0" />
        ) : (
          <Wrench className="size-5 text-muted-foreground shrink-0" />
        )}
        <span className="font-medium text-sm truncate">{module.name || '—'}</span>
      </div>
      <p className="text-xs text-muted-foreground line-clamp-2">{module.type}</p>
    </Card>
  )
}

function ShopModuleCard({ module }: { module: BaseShopModuleModel }) {
  return (
    <Card className="flex flex-col gap-2 p-4">
      <div className="flex items-center gap-2 min-w-0">
        <ShoppingBag className="size-5 text-muted-foreground shrink-0" />
        <span className="font-medium text-sm truncate">{module.type}</span>
      </div>
    </Card>
  )
}

interface Props {
  actionModules: ActionModuleResponse[]
  serviceModules: ServiceModuleModel[]
  shopModules: BaseShopModuleModel[]
}

export function EmployeeModulesSection({ actionModules, serviceModules, shopModules }: Props) {
  const { t } = useTranslation('employees')
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="flex flex-col gap-3">
        <h3 className="font-semibold flex items-center gap-2">
          <Zap className="size-4" />
          {t('detail.modules.actionModules')}
          <Badge variant="secondary" className="ml-auto">{actionModules.length}</Badge>
        </h3>
        {actionModules.length ? (
          actionModules.map((m) => <ActionModuleCard key={m.id} module={m} />)
        ) : (
          <p className="text-sm text-muted-foreground">{t('detail.modules.noActionModules')}</p>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="font-semibold flex items-center gap-2">
          <Wrench className="size-4" />
          {t('detail.modules.serviceModules')}
          <Badge variant="secondary" className="ml-auto">{serviceModules.length}</Badge>
        </h3>
        {serviceModules.length ? (
          serviceModules.map((m) => <ServiceModuleCard key={m.id} module={m} />)
        ) : (
          <p className="text-sm text-muted-foreground">{t('detail.modules.noServiceModules')}</p>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <h3 className="font-semibold flex items-center gap-2">
          <ShoppingBag className="size-4" />
          {t('detail.modules.shopModules')}
          <Badge variant="secondary" className="ml-auto">{shopModules.length}</Badge>
        </h3>
        {shopModules.length ? (
          shopModules.map((m) => <ShopModuleCard key={m.id} module={m} />)
        ) : (
          <p className="text-sm text-muted-foreground">{t('detail.modules.noShopModules')}</p>
        )}
      </div>
    </div>
  )
}
